// File Name: pages/match-pages/MatchShell.js
//
// Shared frame for the two public match pages. These render OUTSIDE the
// marketing site's Header/Footer (see App.js) because they carry the app's dark
// brand, not the site's — someone arriving from a WhatsApp match link should
// feel like they opened YNO, not a charity landing page.

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import './matchTheme.css';
import { PLAY_STORE_URL, SITE_URL, SUPPORT_EMAIL } from './matchConfig';

const FONTS_ID = 'yno-match-fonts';
const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700;800&family=Barlow+Condensed:wght@600;700;800&family=Cairo:wght@400;600;700;800&display=swap';

/**
 * Loads Barlow / Barlow Condensed / Cairo on demand.
 *
 * Injected rather than added to public/index.html so the marketing pages, which
 * use none of these, do not pay for them on every visit.
 */
function useBrandFonts() {
  useEffect(() => {
    if (document.getElementById(FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = FONTS_ID;
    link.rel = 'stylesheet';
    link.href = FONTS_HREF;
    document.head.appendChild(link);
    // Deliberately not removed on unmount: the browser has already paid for it,
    // and a second visit within the session should not re-fetch.
  }, []);
}

/** Paints the document background dark for as long as a match page is mounted. */
function useDarkBody() {
  useEffect(() => {
    document.body.classList.add('ynoDark');
    return () => document.body.classList.remove('ynoDark');
  }, []);
}

export function MatchShell({ t, toggle, lang, children }) {
  useBrandFonts();
  useDarkBody();

  return (
    <div className="ynoPage">
      <div className="wrap">
        <div className="head">
          <Link className="brand" to="/join">
            <div className="mark">
              YN<span>O</span>
            </div>
            <small>{t('brandSub')}</small>
          </Link>
          <button className="langBtn" type="button" onClick={toggle}>
            {lang === 1 ? 'English' : 'العربية'}
          </button>
        </div>

        {children}

        <div className="foot">
          <div>YNO · {t('brandSub')}</div>
          <div>
            <a href={SITE_URL}>nellab.org</a> ·{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * "Get the app" panel, shown on both pages.
 *
 * TODO (Phase 2): when Android App Links ship, add an "Open in the app" button
 * here that fires an intent:// URL for visitors who landed in WhatsApp's in-app
 * browser. Pointless until the app can actually receive the link, so it is not
 * built yet — see WEBSITE_PAGES_SPEC.md §3.7.
 */
export function GetAppCard({ t }) {
  return (
    <div className="card center">
      <p className="eyebrow">{t('getTheApp')}</p>
      {PLAY_STORE_URL ? (
        <a
          className="primary"
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('downloadYno')}
        </a>
      ) : (
        <p className="sub">{t('notOnStores')}</p>
      )}
    </div>
  );
}
